import _ from 'lodash';
import { compareTwoDates } from 'utils/moment';
import { lineItemsService } from './lineItemsService';
import { convertToFloat } from '../utils/math';
import { window } from 'browser-monads';
import { getDeliveryOptionCode, hasValidDeliveryOption } from 'utils/deliveryOptions';

const CHECKOUT_ITEM_SNAPSHOT_KEY = 'ss_checkout_item_snapshots';
const MIN_PAYABLE_PRICE = 0.01;

function normalizePrice(val) {
  const num = Number.parseFloat(String(val ?? '').replace(/[^\d.-]/g, ''));
  return Number.isFinite(num) ? Number(num.toFixed(2)) : 0;
}

function assertPayablePrice(price, context = 'cart item') {
  const normalized = normalizePrice(price);
  if (!Number.isFinite(normalized) || normalized < MIN_PAYABLE_PRICE) {
    throw new Error(`Refusing to add ${context} with non-payable price: ${price}`);
  }
  return normalized;
}

function isHttpUrl(s) {
  return typeof s === 'string' && /^https?:\/\//i.test(s);
}

function getCurrentPageUrl() {
  try {
    const href = window?.location?.href;
    if (isHttpUrl(href)) return href.split('#')[0];
  } catch {}
  return '';
}

function resolveItemUrl(itemData = {}, existingItem = null) {
  const candidates = [
    itemData.itemUrl,
    itemData.url,
    existingItem?.url,
    itemData?.metadata?.productUrl,
    process.env.GATSBY_SITE_URL,
    getCurrentPageUrl(),
  ];
  return candidates.find(isHttpUrl) || '';
}

// Allow http(s), data:image/*, and blob: for cart thumbnails
function isAllowedImgSrc(s) {
  return (
    typeof s === 'string' &&
    (/^https?:\/\//i.test(s) || /^data:image\//i.test(s) || /^blob:/i.test(s))
  );
}

/* ---------- bytes helper (SSR-safe) ---------- */
function jsonBytes(obj) {
  try {
    const s = JSON.stringify(obj);
    if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(s).length;
    // Node fallback
    // eslint-disable-next-line no-undef
    return Buffer.from(s, 'utf8').length;
  } catch {
    return 0;
  }
}

/* ---------- compact metadata ---------- */
function compactMetadata(meta) {
  if (!meta) return {};
  const copy = { ...meta };

  // Strip base64 thumbnail
  if (typeof copy.thumbnail === 'string' && copy.thumbnail.startsWith('data:')) {
    delete copy.thumbnail;
  }

  // Keep under ~7KB (Snipcart soft ~8KB)
  if (jsonBytes(copy) > 7000) {
    delete copy.offer;
    delete copy.user;      // Snipcart collects forms anyway
  }
  return copy;
}

function getMetadataDelivery(meta = {}) {
  return meta.delivery || meta.confirmation?.delivery || {};
}

function assertShippableDelivery(meta = {}, context = 'cart item') {
  const delivery = getMetadataDelivery(meta);
  const code = getDeliveryOptionCode(delivery.code || delivery.option || delivery);
  const option = delivery.option || delivery.name || delivery.shippingOption;
  const deliveryDate = delivery.deliveryDate || meta.confirmation?.delivery?.deliveryDate;

  if (!hasValidDeliveryOption({ name: option, code }) || !deliveryDate) {
    throw new Error(`Refusing to add ${context} without a valid delivery option`);
  }
}

function rememberCheckoutItemSnapshot(item) {
  try {
    if (!window?.sessionStorage || !item) return;
    const current = JSON.parse(window.sessionStorage.getItem(CHECKOUT_ITEM_SNAPSHOT_KEY) || '[]');
    const snapshots = Array.isArray(current) ? current : [];
    const lineItemId = item?.metadata?.lineItemId;
    const id = item?.id;
    const uniqueId = item?.uniqueId;
    const next = snapshots.filter((snapshot) => {
      if (lineItemId && snapshot?.metadata?.lineItemId === lineItemId) return false;
      if (id && snapshot?.id === id) return false;
      if (uniqueId && snapshot?.uniqueId === uniqueId) return false;
      return true;
    });
    next.push(item);
    window.sessionStorage.setItem(CHECKOUT_ITEM_SNAPSHOT_KEY, JSON.stringify(next.slice(-20)));
  } catch {
    // Snapshot storage is only used to enrich the confirmation page.
  }
}

function forgetCheckoutItemSnapshot(item) {
  try {
    if (!window?.sessionStorage || !item) return;
    const current = JSON.parse(window.sessionStorage.getItem(CHECKOUT_ITEM_SNAPSHOT_KEY) || '[]');
    if (!Array.isArray(current) || !current.length) return;

    const matches = [
      item?.metadata?.lineItemId,
      item?.id,
      item?.uniqueId,
    ].filter(Boolean).map(value => String(value));

    if (!matches.length) return;

    const next = current.filter((snapshot) => {
      const snapshotValues = [
        snapshot?.metadata?.lineItemId,
        snapshot?.id,
        snapshot?.uniqueId,
      ].filter(Boolean).map(value => String(value));
      return !matches.some(match => snapshotValues.includes(match));
    });

    if (next.length) {
      window.sessionStorage.setItem(CHECKOUT_ITEM_SNAPSHOT_KEY, JSON.stringify(next));
    } else {
      window.sessionStorage.removeItem(CHECKOUT_ITEM_SNAPSHOT_KEY);
    }
  } catch {
    // Snapshot storage is only used to enrich the confirmation page.
  }
}

/* ---------- Snipcart guards ---------- */
function ensureSnipcart(where = '') {
  const sn = window?.Snipcart;
  if (!sn?.api) throw new Error(`Snipcart not ready${where ? ` (${where})` : ''}`);
  return sn;
}

/* ---------- State helpers ---------- */
function getCartStateItems() {
  try {
    return window?.Snipcart?.store?.getState?.().cart?.items?.items ?? [];
  } catch {
    return [];
  }
}
function getCartItems() {
  return getCartStateItems();
}
function getCartItem(uniqueId) {
  return getCartStateItems().find(i => i.uniqueId === uniqueId);
}

async function removeInvalidPricedItems() {
  const items = getCartStateItems();
  await Promise.all(
    items
      .filter(item => normalizePrice(item?.price) < MIN_PAYABLE_PRICE)
      .map(item => removeItemByUniqueId(item.uniqueId).catch(() => {}))
  );
}

async function removeInvalidCheckoutItems() {
  const items = getCartStateItems();
  await Promise.all(
    items
      .filter((item) => {
        if (normalizePrice(item?.price) < MIN_PAYABLE_PRICE) return true;
        try {
          assertShippableDelivery(item?.metadata || {}, item?.name || item?.id || 'cart item');
          return false;
        } catch {
          return true;
        }
      })
      .map(item => removeItemByUniqueId(item.uniqueId).catch(() => {}))
  );
}

/* ---------- Events ---------- */
function onCartReady() {
  const sn = window?.Snipcart;
  if (!sn?.events?.on) return;
  removeInvalidCheckoutItems();
  sn.events.on('cart.opened', () => {
    removeInvalidCheckoutItems();
  });
  sn.events.on('item.added', () => {
    removeInvalidCheckoutItems();
  });
  sn.events.on('item.removed', (cartItem) => {
    cartItemsService.handleRemoveCartItem(cartItem);
  });
}

/* ---------- Add ---------- */
async function addItemToCart(itemData) {
  ensureSnipcart('add');
  const price = assertPayablePrice(itemData.itemPrice ?? itemData.price, itemData.itemName);
  assertShippableDelivery(itemData.metadata, itemData.itemName);

  const imgs = Array.isArray(itemData.images)
    ? itemData.images.filter(isAllowedImgSrc)
    : [];
  const itemUrl = resolveItemUrl(itemData);

  const payload = {
    id: String(itemData.itemId),
    name: String(itemData.itemName ?? 'Item'),
    price,
    quantity: Number(itemData.quantity) > 0 ? Number(itemData.quantity) : 1,
    ...(itemUrl ? { url: itemUrl } : {}),
    ...(isAllowedImgSrc(itemData.image) ? { image: itemData.image } : {}),
    ...(imgs.length ? { images: imgs } : {}),
    metadata: compactMetadata(itemData.metadata),
  };
  rememberCheckoutItemSnapshot(payload);

  return window.Snipcart.api.cart.items.add(payload);
}

/* ---------- Remove helper ---------- */
async function removeItemByUniqueId(uniqueId) {
  const sn = ensureSnipcart('remove');
  if (sn.api?.cart?.items?.remove) {
    return sn.api.cart.items.remove(uniqueId);
  }
  // legacy path
  if (sn.api?.items?.remove) {
    return sn.api.items.remove(uniqueId);
  }
}

/* ---------- UPDATE → REPLACEMENT ---------- */
async function updateItemToCart(cartItemId, itemData) {
  ensureSnipcart('update');
  const price = assertPayablePrice(itemData.itemPrice ?? itemData.price, itemData.itemName);
  assertShippableDelivery(itemData.metadata, itemData.itemName);

  const items = getCartStateItems();
  const byUnique = items.find(i => i.uniqueId === cartItemId);
  const byLineId = itemData?.metadata?.lineItemId
    ? items.find(i => i.metadata?.lineItemId === itemData.metadata.lineItemId)
    : undefined;
  const byProdId = items.find(i => i.id === String(itemData.itemId));
  const target = byUnique || byLineId || byProdId;

  const imgs = Array.isArray(itemData.images)
    ? itemData.images.filter(isAllowedImgSrc)
    : [];
  const itemUrl = resolveItemUrl(itemData, target);

  const addPayload = {
    id: String(itemData.itemId),
    name: String(itemData.itemName ?? 'Item'),
    price,
    quantity: Number(itemData.quantity) > 0 ? Number(itemData.quantity) : 1,
    // Keep a URL on the line; prefer the one passed, else reuse the existing one
    ...(itemUrl ? { url: itemUrl } : {}),
    ...(isAllowedImgSrc(itemData.image) ? { image: itemData.image } : {}),
    ...(imgs.length ? { images: imgs } : {}),
    metadata: compactMetadata(itemData.metadata),
  };

  if (target) {
    try { await removeItemByUniqueId(target.uniqueId); } catch {}
  }
  rememberCheckoutItemSnapshot(addPayload);
  return window.Snipcart.api.cart.items.add(addPayload);
}

/* ---------- Update a whole cartItem (legacy callers) ---------- */
async function updateCartItemField(cartItem) {
  ensureSnipcart('update field');
  const price = assertPayablePrice(cartItem.price, cartItem.name);
  assertShippableDelivery(cartItem.metadata, cartItem.name);

  const items = getCartStateItems();
  const target = items.find(i => i.uniqueId === cartItem.uniqueId) ||
                 items.find(i => i.metadata?.lineItemId === cartItem?.metadata?.lineItemId);

  const imgs = Array.isArray(cartItem.images)
    ? cartItem.images.filter(isAllowedImgSrc)
    : [];
  const itemUrl = resolveItemUrl(cartItem, target);

  const addPayload = {
    id: String(target?.id ?? cartItem.id),
    name: String(cartItem.name ?? 'Item'),
    price,
    quantity: Number(cartItem.quantity) > 0 ? Number(cartItem.quantity) : 1,
    ...(itemUrl ? { url: itemUrl } : {}),
    ...(isAllowedImgSrc(cartItem.image) ? { image: cartItem.image } : {}),
    ...(imgs.length ? { images: imgs } : {}),
    metadata: compactMetadata(cartItem.metadata),
  };

  if (target) {
    try { await removeItemByUniqueId(target.uniqueId); } catch {}
  }
  rememberCheckoutItemSnapshot(addPayload);
  return window.Snipcart.api.cart.items.add(addPayload);
}

/* ---------- Misc ---------- */
function clearCart() {
  const sn = ensureSnipcart('clear');
  if (sn.api?.cart?.clear) return sn.api.cart.clear();
  if (sn.api?.items?.clear) return sn.api.items.clear();
  return Promise.resolve();
}
function openCart() {
  try {
    const result = window?.Snipcart?.api?.theme?.cart?.open?.();
    if (result && typeof result.catch === 'function') {
      result.catch((error) => {
        if (/NavigationDuplicated|redundant navigation/i.test(String(error?.message || error))) return;
        console.warn('Snipcart openCart error (ignored):', error);
      });
    }
    return result;
  } catch (e) {}
}

function getCartDeliveryCode(cartItem) {
  const delivery = cartItem?.metadata?.delivery || {};
  return getDeliveryOptionCode(delivery.code || delivery.option);
}
function getSameDeliveryItems(addr, dTime, courior) {
  const deliveryCode = getDeliveryOptionCode(courior);
  const cartItems = getCartItems();
  return cartItems.filter(cartItem =>
    compareTwoDates(cartItem.metadata.delivery.dispatchDate, dTime) &&
    compareDeliveryAddresses(addr, cartItem.metadata.user) &&
    getCartDeliveryCode(cartItem) === deliveryCode
  );
}
function compareWithCartItem(addr, dTime, courior, cartItem) {
  const deliveryCode = getDeliveryOptionCode(courior);
  return compareTwoDates(cartItem.metadata.delivery.dispatchDate, dTime) &&
    compareDeliveryAddresses(addr, cartItem.metadata.user) &&
    getCartDeliveryCode(cartItem) === deliveryCode;
}
function compareDeliveryAddresses(addr1, addr2) {
  return addr1.address1.toUpperCase() === addr2.address1.toUpperCase() &&
    (addr1.address2 || '').toUpperCase() === (addr2.address2 || '').toUpperCase() &&
    addr1.country.toUpperCase() === addr2.country.toUpperCase() &&
    addr1.locality.toUpperCase() === addr2.locality.toUpperCase() &&
    String(addr1.postcode) === String(addr2.postcode) &&
    (addr1.state || '').toUpperCase() === (addr2.state || '').toUpperCase();
}

async function handleRemoveCartItem(cartItem) {
  forgetCheckoutItemSnapshot(cartItem);

  try {
    // remove lineItem from db
    lineItemsService.removeLineItem(cartItem.metadata.lineItemId);

    if (cartItem.metadata.delivery.price !== "$0.00") {
      const matchedItems = getSameDeliveryItems(
        cartItem.metadata.user,
        cartItem.metadata.delivery.dispatchDate,
        cartItem.metadata.delivery.option
      );
      if (matchedItems.length > 0) {
        await lineItemsService.updateLineItemDeliveryPrice(
          matchedItems[0].metadata.lineItemId,
          cartItem.metadata.delivery.price
        );
        const newItemData = { ...matchedItems[0] };
        newItemData.metadata.delivery.price = cartItem.metadata.delivery.price;
        const recalculatedTotal = convertToFloat(
          convertToFloat(newItemData.price) + convertToFloat(newItemData.metadata.delivery.price)
        );
        newItemData.metadata.price.totalNumber = recalculatedTotal;
        newItemData.metadata.price.total = `$ ${recalculatedTotal.toFixed(2)}`;
        newItemData.price = recalculatedTotal;
        await updateCartItemField(newItemData);
      }
    }
  } catch {
    // non-fatal
  }
}

async function handleUpdateCartItem(cartItem) {
  try {
    const matchedItems = getSameDeliveryItems(
      cartItem.metadata.user,
      cartItem.metadata.delivery.dispatchDate,
      cartItem.metadata.delivery.option
    );
    if (matchedItems.length > 0) {
      await lineItemsService.updateLineItemDeliveryPrice(
        matchedItems[0].metadata.lineItemId,
        cartItem.metadata.delivery.price
      );
      const newItemData = { ...matchedItems[0] };
      newItemData.metadata.delivery.price = cartItem.metadata.delivery.price;
      const recalculatedTotal = convertToFloat(
        convertToFloat(newItemData.price) + convertToFloat(newItemData.metadata.delivery.price)
      );
      newItemData.metadata.price.totalNumber = recalculatedTotal;
      newItemData.metadata.price.total = `$ ${recalculatedTotal.toFixed(2)}`;
      newItemData.price = recalculatedTotal;
      await updateCartItemField(newItemData);
    }
  } catch {
    // non-fatal
  }
}

export const cartItemsService = {
  getCartItems,
  getCartItem,
  addItemToCart,
  updateItemToCart,          // now remove→add internally (quiet)
  clearCart,
  openCart,
  removeInvalidPricedItems,
  removeInvalidCheckoutItems,
  getSameDeliveryItems,
  compareDeliveryAddresses,
  handleRemoveCartItem,
  handleUpdateCartItem,
  onCartReady,
  compareWithCartItem,
};
